/**
 * OYUNLASTIRMA — API ile arayuzun paylastigi sozlesme (F-GAM, dalga 1).
 *
 * Kural ICERIGI burada dogrulanir: miktarlar kodda degil veritabaninda (`PointRuleVersion.rules`, F-GAM-03), ama
 * veritabanindan okunan JSON'a guvenilmez — her okuma bu semadan gecer. Bozuk ya da eksik alanli bir surum puan
 * hesabina GIRMEZ (servis reddeder); "eksik alan 0 sayilir" gibi sessiz bir varsayim yanlis puan dagitirdi.
 */
import { z } from "zod";
import { GEREKCE_AGIR_EN_AZ, gerekceSemasi } from "./gerekce";

const pozitif = z.number().int().positive();
const sifirVeUstu = z.number().int().min(0);

/** F-GAM-06/07 — gorev puani ve uygunluk esikleri. */
const gorevKuraliSemasi = z
  .object({
    tam: sifirVeUstu,
    az: sifirVeUstu,
    gecTam: sifirVeUstu,
    gecAz: sifirVeUstu,
    gunTavani: pozitif,
    hedefsizTamSoru: pozitif,
    tamSureDk: pozitif,
    azSureDk: pozitif,
  })
  .strict()
  .refine((g) => g.azSureDk <= g.tamSureDk, {
    message: "AZ süre eşiği TAM eşiğinden büyük olamaz.",
  })
  .refine((g) => g.gecTam <= g.tam && g.gecAz <= g.az, {
    message: "Geç giriş puanı zamanında girişten büyük olamaz.",
  });

/** F-GAM-06 — supheli kayit esikleri. */
const supheliKuraliSemasi = z
  .object({
    hedefKati: pozitif,
    soruBasinaAsgariSn: pozitif,
    gorevAzamiSaat: pozitif,
  })
  .strict();

const haftaEsigiSemasi = z.object({ oran: z.number().gt(0).max(1), puan: pozitif }).strict();

/** F-GAM-08 — haftalik uyum. Esikler buyukten kucuge; yalniz ulasilan EN YUKSEK esik odenir. */
const haftaKuraliSemasi = z
  .object({
    esikler: z.array(haftaEsigiSemasi).min(1).max(5),
    haftaAsgariGorev: pozitif,
  })
  .strict()
  .refine((h) => h.esikler.every((e, i) => i === 0 || e.oran < h.esikler[i - 1]!.oran), {
    message: "Haftalık eşikler orana göre büyükten küçüğe ve tekil olmalı.",
  });

/**
 * F-GAM-18 (dalga 2, surum 7b) — davet odulu. Miktarlar KODDA DEGIL, kural surumunde (Emre S8: eden 500, edilen 200;
 * S9: ilk odemeden 14 gun sonra). Alan OPTIONAL: surumde yoksa davet puani HIC yazilmaz (sessiz varsayilan yok).
 * Bir tarafin miktari 0 ise o tarafa hareket yazilmaz (defterde INVITE yalniz arti olabilir). Veritabani tetikleyicisi
 * (`puan_davet_eslesme`, oyun_2b) yazilan tutari bu alanla karsilastirir.
 */
export const davetKuraliSemasi = z
  .object({
    davetEden: sifirVeUstu.max(100_000),
    davetEdilen: sifirVeUstu.max(100_000),
    /** Cayma suresi: odul ilk odemenin `paidAt`inden bu kadar gun sonra kesinlesir. */
    beklemeGun: z.number().int().min(1).max(90),
  })
  .strict();
export type DavetKurali = z.infer<typeof davetKuraliSemasi>;

export const oyunKuralSemasi = z
  .object({
    gorev: gorevKuraliSemasi,
    supheli: supheliKuraliSemasi,
    hafta: haftaKuraliSemasi,
    deneme: z.object({ puan: sifirVeUstu }).strict(),
    /** F-GAM-10 */
    koc: z.object({ hareketTavani: pozitif, haftalikOgrenciTavani: pozitif }).strict(),
    /** F-GAM-11 */
    kampanya: z.object({ instagram: sifirVeUstu }).strict(),
    /** F-GAM-14 */
    sure: z.object({ hareketsizAy: pozitif, bildirimGunOnce: pozitif }).strict(),
    /** F-GAM-18 (7b) — yoksa davet puani yazilmaz. Surum 1'de YOK (Emre yeni surumle girer). */
    davet: davetKuraliSemasi.optional(),
  })
  .strict()
  .refine((k) => k.koc.hareketTavani <= k.koc.haftalikOgrenciTavani, {
    message: "Koç hareket tavanı haftalık öğrenci tavanından büyük olamaz.",
  });

export type OyunKurali = z.infer<typeof oyunKuralSemasi>;

/**
 * KURAL SURUMU 1 — Emre 06.10.2026 (kucuk olcek) + koordinator K2 (uygunluk esikleri). Migration
 * `20261008090000_oyun_1` AYNI icerigi yazar; tutarlilik testi (`oyun-kural.test.ts`) ikisini karsilastirir.
 */
export const OYUN_KURALI_V1: OyunKurali = {
  gorev: {
    tam: 10,
    az: 4,
    gecTam: 5,
    gecAz: 2,
    gunTavani: 50,
    hedefsizTamSoru: 10,
    tamSureDk: 20,
    azSureDk: 5,
  },
  supheli: { hedefKati: 3, soruBasinaAsgariSn: 6, gorevAzamiSaat: 6 },
  hafta: {
    esikler: [
      { oran: 1, puan: 60 },
      { oran: 0.8, puan: 30 },
    ],
    haftaAsgariGorev: 5,
  },
  deneme: { puan: 20 },
  koc: { hareketTavani: 50, haftalikOgrenciTavani: 100 },
  kampanya: { instagram: 10 },
  sure: { hareketsizAy: 12, bildirimGunOnce: 30 },
};

/** Puan uygunlugu — renkten AYRI (F-GAM-06). */
export type PuanUygunlugu = "TAM" | "AZ" | "YOK" | "SUPHELI";

export const PUAN_UYGUNLUGU_ETIKETI: Record<PuanUygunlugu, string> = {
  TAM: "Tam puan",
  AZ: "Kısmi puan",
  YOK: "Puan yok",
  SUPHELI: "İncelemede",
};

/** Hareket turu — ekranda gorunen ad. */
export const PUAN_HAREKETI_ETIKETI: Record<string, string> = {
  TASK_COMPLETE: "Görevler",
  WEEK_COMPLETION: "Haftalık uyum",
  EXAM_PARTICIPATION: "Deneme",
  COACH_GOAL: "Koçundan",
  CAMPAIGN: "Teşekkür",
  ADMIN_ADJUST: "Düzeltme",
  EXPIRE: "Süresi doldu",
  // F-GAM-16 (dalga 2, 7a) — magaza
  PURCHASE: "Ödül",
  PURCHASE_REFUND: "Ödül iadesi",
  PURCHASE_REFUND_OFFSET: "İade dengesi (geri alınan puan)",
  // F-GAM-18/19 (dalga 2, 7b)
  WHEEL_SPIN: "Çark",
  INVITE: "Davet",
};

// --------------------------------------------------------------------------- istekler

/** Istemci istek kimligi — yeniden denemede ayni hareket doner (tekillik `MANUEL:{kimlik}`). */
const istekKimligi = z.string().uuid();

/** F-GAM-10 — kocun elle puani. Ust sinir kural surumunden sunucuda; burada yalniz bicim. */
export const kocPuanIstegiSemasi = z
  .object({
    miktar: z.number().int().positive().max(1000),
    gerekce: gerekceSemasi(),
    istekKimligi,
  })
  .strict();
export type KocPuanIstegi = z.infer<typeof kocPuanIstegiSemasi>;

/** F-GAM-10 — yoneticinin gerekceli +/- duzeltmesi (sudo). */
export const puanDuzeltmeSemasi = z
  .object({
    miktar: z
      .number()
      .int()
      .refine((m) => m !== 0, { message: "Düzeltme miktarı sıfır olamaz." })
      .refine((m) => Math.abs(m) <= 100_000, { message: "Düzeltme miktarı çok büyük." }),
    gerekce: gerekceSemasi({ enAz: GEREKCE_AGIR_EN_AZ }),
    istekKimligi,
    ilgiliHareketId: z.string().min(1).max(40).nullish(),
  })
  .strict();
export type PuanDuzeltme = z.infer<typeof puanDuzeltmeSemasi>;

/** F-GAM-03 — yeni kural surumu (sudo). `gecerliBaslangic`: Pazartesi, YYYY-MM-DD. */
export const kuralSurumuIstegiSemasi = z
  .object({
    kurallar: oyunKuralSemasi,
    gecerliBaslangic: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    gerekce: gerekceSemasi({ enAz: GEREKCE_AGIR_EN_AZ }),
  })
  .strict();
export type KuralSurumuIstegi = z.infer<typeof kuralSurumuIstegiSemasi>;

// --------------------------------------------------------------------------- yanitlar

export interface PuanHareketiOzeti {
  id: string;
  tur: string;
  miktar: number;
  /** Hak degisimi; `miktar`dan farkliysa bakiyede kesilmis geri alma (F-GAM-05) */
  istenenMiktar: number;
  bakiyeSonrasi: number;
  hafta: string | null;
  /** Ogrenciye gorunen gerekce — KVKK'da silinmisse null */
  not: string | null;
  tarih: string;
}

export interface BekleyenGorevSatiri {
  gorevId: string;
  gun: string;
  seviye: PuanUygunlugu;
  gecGiris: boolean;
  puan: number;
}

export interface BekleyenOzet {
  /** Pazartesi, YYYY-MM-DD */
  hafta: string;
  gorevPuani: number;
  haftaPuani: number;
  denemePuani: number;
  toplam: number;
  /** Puana esas oran (TAM / degerlendirilebilir) — 0..1 */
  oran: number;
  gorevler: BekleyenGorevSatiri[];
}

export interface RozetOzeti {
  kod: string;
  ad: string;
  aciklama: string;
  kazanildi: string | null;
}

export interface OyunOgrenciOzeti {
  /** Modul kapaliysa uc 404 doner; acikken her zaman true */
  acik: true;
  bakiye: number;
  /** Puanin basladigi ilk Pazartesi (F-GAM-12) — oncesinde bekleyen de yok */
  baslangic: string | null;
  bekleyen: BekleyenOzet[];
  sonHareketler: PuanHareketiOzeti[];
  rozetler: RozetOzeti[];
  kampanyaKatildi: boolean;
  /** 12 ay kurali uyarisi (F-GAM-14) — yoksa null */
  sureUyarisi: { dusmeTarihi: string } | null;
  kural: OyunKurali | null;
}
